// views/Dashboard/Referrals/Referrals.tsx
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import Modal from '../../../components/common/Modal/Modal';
import Loading from '../../../components/common/Loading/Loading';
import { Referenced, ReferralCode, Transaction, User } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './Referrals.module.css';

// ── Czech bank-account validation (mod 11 checksums) ──────────
function mod11Checksum(number: string, weights: number[]): boolean {
  const digits = number.padStart(weights.length, '0').split('').map(Number);
  let sum = 0;
  for (let i = 0; i < weights.length; i++) sum += digits[i] * weights[i];
  return sum % 11 === 0;
}

const checkBankAccountValidity = (accountStr: string): boolean => {
  const match = accountStr.match(/^(?:(\d{0,6})-)?(\d{1,10})\/(\d{4})$/);
  if (!match) return false;
  const prefix = match[1] || '0';
  const account = match[2];
  const bank = match[3];
  if (prefix !== '0' && !mod11Checksum(prefix, [10, 5, 8, 4, 2, 1])) return false;
  if (!mod11Checksum(account.padStart(10, '0'), [6, 3, 7, 9, 10, 5, 8, 4, 2, 1])) return false;
  return /^\d{4}$/.test(bank);
};

const hideBankAccount = (bankAccount: string): string => {
  const visiblePart = bankAccount.slice(bankAccount.length - 9);
  return '*'.repeat(bankAccount.length - visiblePart.length) + visiblePart;
};

interface ResultState {
  success: boolean;
  reason?: string;
  amount?: number;
}

const Referrals = () => {
  const { user, setUser } = useAuth();
  const [copied, setCopied] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(true);
  const [referencedUsers, setReferencedUsers] = useState<any[]>([]);
  const [referralCodes, setReferralCodes] = useState<any[]>([]);
  const [currentBankAccount, setCurrentBankAccount] = useState(user?.bank_account || '');
  const [showBankChangeModal, setShowBankChangeModal] = useState(false);
  const [bankChangeResult, setBankChangeResult] = useState<ResultState | null>(null);
  const [rememberBankAccount, setRememberBankAccount] = useState(false);
  const [localBankAccount, setLocalBankAccount] = useState(!user?.bank_account);

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawResult, setWithdrawResult] = useState<ResultState | null>(null);
  const [processing, setProcessing] = useState(false);
  const [autoWithdrawProcessing, setAutoWithdrawProcessing] = useState(false);
  const [referralStats, setReferralStats] = useState<any[]>([]);

  const navigate = useNavigate();

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      setReferencedUsers(await Referenced.getAll());
      setReferralStats(await Transaction.getStats('half_year'));
      setReferralCodes(await ReferralCode.getAll());
      setLoading(false);
    } catch (error) {
      console.log(error);
      notify.error('Nepodařilo se načíst data');
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const changeBankAccountHandler = async () => {
    if (!user) return;
    try {
      setProcessing(true);
      const updatedUser = new User(user);
      updatedUser.bank_account = currentBankAccount;
      if (rememberBankAccount) await updatedUser.update();
      setLocalBankAccount(!rememberBankAccount);
      setBankChangeResult({ success: true });
      setUser(updatedUser);
    } catch (error: any) {
      console.error(error);
      setBankChangeResult({ success: false, reason: error?.message });
    } finally {
      setProcessing(false);
    }
  };

  const deleteBankAccountHandler = async () => {
    if (!user) return;
    try {
      setProcessing(true);
      const updatedUser = new User(user);
      updatedUser.bank_account = null;
      if (!localBankAccount) await updatedUser.update();
      setCurrentBankAccount('');
      setUser(updatedUser);
    } catch (error) {
      console.error(error);
      notify.error('Smazání bankovního účtu se nepovedlo');
    } finally {
      setProcessing(false);
    }
  };

  const onAutoWithdrawChange = async () => {
    if (!user) return;
    try {
      setAutoWithdrawProcessing(true);
      const updatedUser = new User(user);
      updatedUser.auto_withdraw = !updatedUser.auto_withdraw;
      await updatedUser.update();
      setUser(updatedUser);
    } catch (error) {
      console.error(error);
      notify.error('Nepodařilo se změnit automatické vybírání');
    } finally {
      setAutoWithdrawProcessing(false);
    }
  };

  const resetBankChanging = () => {
    setShowBankChangeModal(false);
    setBankChangeResult(null);
  };

  const handleCopyLink = (referralCodeId: number, referralLink: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(referralLink);
        setCopied(referralCodeId);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = referralLink;
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(referralCodeId);
      }
    } catch (error) {
      console.error('not copied');
      setCopied('Nelze zkopírovat');
    } finally {
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleWithdraw = async () => {
    if (!user) return;
    try {
      setProcessing(true);
      const amount = parseFloat(withdrawAmount);
      await Transaction.create({ amount, currentBankAccount });

      // update User
      setUser(await User.get());

      setWithdrawResult({ success: true, amount });

    } catch (error: any) {
      console.error(error);
      setWithdrawResult({ success: false, reason: error?.message });
    } finally {
      setProcessing(false);
    }
  };

  const resetWithdraw = () => {
    setShowWithdrawModal(false);
    setWithdrawAmount('');
    setWithdrawResult(null);
    setProcessing(false);
  };

  if (loading || !user) return <Loading />;

  const maxEarnings = Math.max(...referralStats.map((d) => d.reached_profit), 0);

  return (
    <div className={styles.referralsPage}>
      <h1>Referral Program</h1>

      <div className={styles.statsGrid} data-tour="ref-stats">
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>👥</div>
          <div className={styles.statContent}>
            <h3>Celkem doporučení</h3>
            <p className={styles.statValue}>{referencedUsers.length}</p>
          </div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>💳</div>
          <div className={styles.statContent}>
            <h3>K výplatě</h3>
            <p className={styles.statValue}>{user.balance} Kč</p>
          </div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statIcon}>💰</div>
          <div className={styles.statContent}>
            <h3>Celkové výdělky z referralů</h3>
            <p className={styles.statValue}>{user.referral_revenue} Kč</p>
          </div>
        </Card>
      </div>

      <Card className={styles.referralLinkCard} data-tour="ref-link">
        <h2>Váš referral odkaz</h2>
        <p>Sdílejte tento odkaz s přáteli a vydělávejte</p>

        {referralCodes.map((referralCode) => {
          const referralLink = `${window.location.origin}/referral?referralCode=${referralCode.code}`;
          const referralText = `Posílám Ti pozvánku na platformu DigiBrigi.cz, kde si můžeš online přivydělat až 25 000 Kč měsíčně. Další pokyny obdržíš po zadání svého mailu. ${referralLink}`;
          return (
            <div className={styles.linkContainer} key={referralCode.id}>
              <input type="text" value={referralText} readOnly className={`form-input ${styles.linkInput}`} disabled />
              <Button onClick={() => handleCopyLink(referralCode.id, referralText)}>
                {copied === referralCode.id ? 'Zkopírováno' : 'Zkopírovat'}
              </Button>
            </div>
          );
        })}
      </Card>

      <div className={styles.payoutSection}>
        <Card className={styles.payoutSummary} data-tour="ref-payout">
          <h2>Výplaty</h2>
          <div className={styles.payoutStats}>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>K výplatě:</span>
              <span className={styles.payoutValue}>{Math.round(user.balance)} Kč</span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Čekající na vyplacení:</span>
              <span className={styles.payoutValue}>{Math.round(user.pending_paid_out)} Kč</span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Již vyplaceno:</span>
              <span className={styles.payoutValue}>{Math.round(user.paid_out)} Kč</span>
            </div>
            <div className={styles.payoutItem}>
              <span className={styles.payoutLabel}>Bankovní účet:</span>
              <span className={styles.payoutAccount}>{user.bank_account ? hideBankAccount(user.bank_account) : 'Not set yet'}</span>
              {user.bank_account && (
                <Button variant="warning" size="small" disabled={processing} onClick={deleteBankAccountHandler}>
                  Odstranit
                </Button>
              )}
            </div>
          </div>
          <Button
            variant="primary"
            className={styles.withdrawButton}
            onClick={() => (!user.bank_account ? setShowBankChangeModal(true) : setShowWithdrawModal(true))}
          >
            {!user.bank_account ? 'nastavit bankovní účet pro výplatu' : 'Vyplatit peníze'}
          </Button>
        </Card>
      </div>

      <div className={styles.payoutSection}>
        <Card className={styles.payoutSummary} data-tour="ref-auto-withdraw">
          <h2>Automatický výběr</h2>
          <h4>Máte možnost povolit měsíční automatický výběr</h4>
          <p>
            V takovém případě Vám budou připisovány všechny vaše peněžní prostředky automaticky. A to vždy nejpozději do 7. dne
            nového kalendářního měsíce
          </p>

          {user.bank_account && !localBankAccount ? (
            <Button
              variant={user.auto_withdraw ? 'primary' : 'secondary'}
              className={styles.autoWithdrawButton}
              onClick={onAutoWithdrawChange}
              disabled={autoWithdrawProcessing}
            >
              {user.auto_withdraw ? 'zapnuto' : 'vypnuto'}
            </Button>
          ) : (
            <p className={styles.warningText}> Nejprve je ale potřeba si nastavit a nechat zapamatovat Vaše číslo účtu</p>
          )}
        </Card>
      </div>

      <Card className={styles.earningsChart}>
        <h2>Vývoj výdělků z referralů</h2>
        <div className={styles.chart}>
          <div className={styles.yAxis}>
            <span>{maxEarnings} Kč</span>
            <span>{Math.round(maxEarnings * 0.75)} Kč</span>
            <span>{Math.round(maxEarnings * 0.5)} Kč</span>
            <span>{Math.round(maxEarnings * 0.25)} Kč</span>
            <span>0 Kč</span>
          </div>
          <div className={styles.bars}>
            {referralStats.map((item, index) => {
              const heightPercent = maxEarnings > 0 ? (item.reached_profit / maxEarnings) * 100 : 0;
              return (
                <div key={index} className={styles.barWrapper}>
                  <div className={styles.barContainer}>
                    <div className={styles.bar} style={{ height: `${heightPercent}%` }} title={`${item.reached_profit} Kč`} />
                  </div>
                  <span className={styles.label}>{item.month}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <Card className={styles.referralsTable}>
        <h2>Vaše doporučení</h2>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nickname</th>
                <th>Předplatné</th>
                <th>Datum registrace</th>
                <th>Výdělek</th>
              </tr>
            </thead>
            <tbody>
              {referencedUsers.map((refUser) => (
                <tr key={refUser.name}>
                  <td className={styles.nickname}>{refUser.name}</td>
                  <td>
                    <span className={`${styles.membership} ${styles[String(refUser.tier_name).toLowerCase()]}`}>{refUser.tier_name}</span>
                  </td>
                  <td>{new Date(refUser.creation_timestamp * 1000).toLocaleDateString('cs-CZ')}</td>
                  <td className={styles.earnings}>{Number(refUser.referencer_reward)} Kč</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileCards}>
          {referencedUsers.map((refUser, idx) => (
            <div key={idx} className={styles.mobileCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardName}>{refUser.name}</div>
                <div className={styles.cardDate}>{new Date(refUser.creation_timestamp * 1000).toLocaleDateString('cs-CZ')}</div>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.cardRow}>
                  <span>Předplatné:</span>
                  <span className={`${styles.membership} ${styles[String(refUser.tier_name).toLowerCase()]}`}>{refUser.tier_name}</span>
                </div>
              </div>
              <div className={styles.cardFooter}>
                <div className={styles.earnings}>{Number(refUser.referencer_reward)} Kč</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Withdraw modal */}
      {showWithdrawModal && (
        <Modal isOpen onClose={resetWithdraw} title="Výběr peněz">
          {!withdrawResult ? (
            <div className={styles.modalBody}>
              <p>
                Dostupné k výplatě: <strong>{Math.round(user.balance)} Kč</strong>
              </p>
              <p>
                Bankovní účet: <strong>{user.bank_account ? hideBankAccount(user.bank_account) : ''}</strong>
              </p>

              <div className={styles.inputGroup}>
                <label>Částka k výběru:</label>
                <input
                  type="number"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Zadejte částku"
                  max={Math.round(user.balance)}
                  className={styles.withdrawInput}
                />
              </div>

              <div className={styles.modalActions}>
                <Button variant="secondary" onClick={resetWithdraw}>
                  Zrušit
                </Button>
                <Button
                  variant="primary"
                  onClick={handleWithdraw}
                  disabled={!withdrawAmount || parseFloat(withdrawAmount) > user.balance || processing || !currentBankAccount}
                >
                  {processing ? 'Zpracovává se...' : 'Potvrdit výběr'}
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.modalBody}>
              <div className={`${styles.withdrawResult} ${withdrawResult.success ? styles.success : styles.error}`}>
                <div className={styles.resultIcon}>{withdrawResult.success ? '✅' : '❌'}</div>
                <h4>{withdrawResult.success ? 'Výběr úspěšný!' : 'Výběr neúspěšný'}</h4>
                <p>
                  {withdrawResult.success
                    ? `${withdrawResult.amount} Kč bylo úspěšně zařazeno do fronty pro výběr. Peníze dorazí nejpozději do 7. dne následujiciho kalendářního měsíce.`
                    : withdrawResult.reason}
                </p>
              </div>
              <div className={styles.modalActions}>
                <Button variant="primary" onClick={resetWithdraw}>
                  Zavřít
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Bank account modal */}
      {showBankChangeModal && (
        <Modal isOpen onClose={resetBankChanging} title="Změna/nastavení bankovního účtu">
          {!bankChangeResult ? (
            <div className={styles.modalBody}>
              <div className={styles.inputGroup}>
                <label>Číslo účtu:</label>
                <input
                  value={currentBankAccount}
                  onChange={(e) => setCurrentBankAccount(e.target.value)}
                  placeholder="Zadejte číslo účtu"
                  className={styles.withdrawInput}
                />
              </div>

              <div className={styles.modalActions}>
                <div className={styles.checkboxGroup}>
                  <label className={styles.checkbox}>
                    <input type="checkbox" checked={rememberBankAccount} onChange={() => setRememberBankAccount((prev) => !prev)} />
                    <span>Zapamatovat?</span>
                  </label>
                </div>
                <Button variant="secondary" onClick={resetBankChanging}>
                  Zrušit
                </Button>
                <Button
                  variant="primary"
                  onClick={changeBankAccountHandler}
                  disabled={!currentBankAccount || processing || !checkBankAccountValidity(currentBankAccount)}
                >
                  {processing ? 'Zpracovává se...' : 'Potvrdit změnu'}
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.modalBody}>
              <div className={`${styles.withdrawResult} ${bankChangeResult.success ? styles.success : styles.error}`}>
                <div className={styles.resultIcon}>{bankChangeResult.success ? '✅' : '❌'}</div>
                <h4>{bankChangeResult.success ? 'Úspěšně nastaveno' : bankChangeResult.reason}</h4>
              </div>
              <div className={styles.modalActions}>
                <Button variant="primary" onClick={resetBankChanging}>
                  Zavřít
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Referrals;
